import React, { useState } from 'react';
import { useGetAllOrders } from '../../hooks/useProductionPlanning';
import type { ProductionOrderFilters, ProductionOrder, PaginatedResponse } from '../../types/production.types';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Download, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import ExcelJS from 'exceljs';
import { getMexicoDateString } from '../../lib/mexicoDate';
import { useAuth } from '../../hooks/useAuth';

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200];
const ALL_LIMIT = 9999;

const MasterListView: React.FC = () => {
  const { user } = useAuth();
  const [filters, setFilters] = useState<ProductionOrderFilters>({
    page: 1,
    limit: 20,
    status: '',
    priority: '',
    orderType: '',
    orderNumber: '',
    customerName: '',
    branchName: '',
    machineName: '',
  });

  const { data, isLoading, error } = useGetAllOrders(filters);

  const handleFilterChange = (key: keyof ProductionOrderFilters, value: string | number) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : prev.page,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const clearFilters = () => {
    setFilters(prev => ({
      page: 1,
      limit: prev.limit,
      status: '',
      priority: '',
      orderType: '',
      orderNumber: '',
      customerName: '',
      branchName: '',
      machineName: '',
    }));
  };

  // Same logic as ProductionOrders: actualStartDate + !actualEndDate = En Producción
  const getStatusLabel = (order: ProductionOrder) => {
    if (order.actualStartDate && !order.actualEndDate) return 'En Producción';
    switch (order.status) {
      case 'CREATED': return 'Creada';
      case 'AUTHORIZED': return 'Autorizada';
      case 'COMPLETED': return 'Finalizada';
      case 'CLOSED': return 'Cerrada';
      default: return order.status;
    }
  };

  const getStatusColor = (order: ProductionOrder) => {
    if (order.actualStartDate && !order.actualEndDate) return 'bg-blue-100 text-blue-800';
    switch (order.status) {
      case 'CREATED': return 'bg-yellow-100 text-yellow-800';
      case 'AUTHORIZED': return 'bg-green-100 text-green-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case '1': return 'Alta';
      case '2': return 'Media';
      case '3': return 'Baja';
      default: return priority;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case '1': return 'bg-red-100 text-red-800';
      case '2': return 'bg-yellow-100 text-yellow-800';
      case '3': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getOrderTypeLabel = (orderType?: string | null) => {
    switch (orderType) {
      case 'PRODUCTO_NUEVO': return 'Producto Nuevo';
      case 'REPETICION': return 'Repetición';
      default: return 'Sin Definir';
    }
  };

  const getOrderTypeColor = (orderType?: string | null) => {
    switch (orderType) {
      case 'PRODUCTO_NUEVO': return 'bg-purple-100 text-purple-800';
      case 'REPETICION': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  const downloadExcel = async () => {
    if (orders.length === 0) {
      alert('No hay órdenes para descargar');
      return;
    }
    const dataToExport = orders.map(order => ({
      'N° Orden': order.orderNumber,
      'Cliente': order.customerName || '-',
      'Código Cliente': order.customerCode || '-',
      'Sucursal': order.branchName || '-',
      'Producto': order.productName,
      'Código Producto': order.productCode,
      'Cantidad': `${Number(order.quantityPlanned).toFixed(2)} ${order.unit}`,
      'Prioridad': getPriorityLabel(order.priority),
      'Tipo de Proceso': getOrderTypeLabel(order.orderType),
      'Estado': getStatusLabel(order),
      'Fecha Entrega': formatDate(order.dueDate),
      'Máquina': order.machineName || '-',
      'Lote': order.lotCode || '-',
    }));
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Órdenes');
    if (dataToExport.length > 0) {
      worksheet.columns = Object.keys(dataToExport[0]).map((key, i) => ({
        header: key, key,
        width: [15, 25, 15, 18, 30, 15, 15, 12, 15, 12, 15, 15, 15][i] ?? 15,
      }));
      dataToExport.forEach(row => worksheet.addRow(row));
    }
    const filename = `Lista_Ordenes_${getMexicoDateString()}.xlsx`;
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  // ROT-021: orders:master-list decouples this menu item/page from
  // orders:list (previously shared with Tablero de Planeación and Órdenes
  // de Producción, so none of the three could be shown/hidden
  // independently). There's no route guard anywhere in the app —
  // AppLayout.tsx only hides the nav link — so this check is what actually
  // blocks direct navigation to the URL for a role that doesn't have it.
  if (!user?.permissions?.includes('orders:master-list')) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Sin acceso</p>
          <p>No tienes permiso para ver la Lista Maestra.</p>
          <p className="mt-1 text-xs">El permiso <code>orders:master-list</code> no está asignado a tu rol. Pide al administrador que lo asigne en Roles y Permisos, luego vuelve a iniciar sesión.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold mb-2">Error al cargar órdenes</p>
          <p>{(error as Error).message}</p>
        </div>
      </div>
    );
  }

  const responseData = data as PaginatedResponse<ProductionOrder> | undefined;
  const orders = responseData?.data || [];
  const pagination = responseData?.pagination;
  const showingAll = filters.limit === ALL_LIMIT;

  // Smart page numbers: always show first, last, current±2, with ellipsis
  const getPageNumbers = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [];
    const around = new Set([1, total, current - 1, current, current + 1].filter(p => p >= 1 && p <= total));
    let prev = 0;
    Array.from(around).sort((a, b) => a - b).forEach(p => {
      if (p - prev > 1) pages.push('...');
      pages.push(p);
      prev = p;
    });
    return pages;
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📊 Lista Maestra de Órdenes</h1>
            <p className="text-sm text-gray-600 mt-1">Vista completa de todas las órdenes de producción</p>
          </div>
          <Button onClick={downloadExcel} className="bg-green-600 hover:bg-green-700 text-white">
            <Download className="w-4 h-4 mr-2" />
            Descargar Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">N° Orden</label>
            <Input
              type="text"
              placeholder="Buscar por número..."
              value={filters.orderNumber}
              onChange={(e) => handleFilterChange('orderNumber', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
            <Input
              type="text"
              placeholder="Buscar por cliente..."
              value={filters.customerName}
              onChange={(e) => handleFilterChange('customerName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
            <Input
              type="text"
              placeholder="Buscar por sucursal..."
              value={filters.branchName}
              onChange={(e) => handleFilterChange('branchName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="CREATED">Creada</option>
              <option value="AUTHORIZED">Autorizada</option>
              <option value="COMPLETED">Finalizada</option>
              <option value="CLOSED">Cerrada</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Prioridad</label>
            <select
              value={filters.priority}
              onChange={(e) => handleFilterChange('priority', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todas</option>
              <option value="1">Alta</option>
              <option value="2">Media</option>
              <option value="3">Baja</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Máquina</label>
            <Input
              type="text"
              placeholder="Buscar por máquina..."
              value={filters.machineName}
              onChange={(e) => handleFilterChange('machineName', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Proceso</label>
            <select
              value={filters.orderType}
              onChange={(e) => handleFilterChange('orderType', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="PRODUCTO_NUEVO">Producto Nuevo</option>
              <option value="REPETICION">Repetición</option>
              <option value="SIN_DEFINIR">Sin Definir</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={clearFilters} variant="outline" size="sm">
            Limpiar Filtros
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando órdenes...</p>
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-gray-500">
              <p className="text-xl font-semibold mb-2">No se encontraron órdenes</p>
              <p className="text-sm">Intenta ajustar los filtros</p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Orden</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sucursal</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Producto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo de Proceso</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Entrega</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Máquina</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order: ProductionOrder) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {order.orderNumber}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {order.customerName || '-'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {order.branchName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="max-w-xs truncate font-medium" title={order.productName}>{order.productName}</div>
                      <div className="text-xs text-gray-400">{order.productCode}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {Number(order.quantityPlanned).toFixed(2)} {order.unit}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getPriorityColor(order.priority)}`}>
                        {getPriorityLabel(order.priority)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getOrderTypeColor(order.orderType)}`}>
                        {getOrderTypeLabel(order.orderType)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {formatDate(order.dueDate)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(order)}`}>
                        {getStatusLabel(order)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {order.machineName || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination / Page-size bar */}
      <div className="bg-white border-t border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">

          {/* Left: total + page-size selector */}
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              {pagination ? (
                showingAll
                  ? <><span className="font-medium">{pagination.total}</span> registros en total</>
                  : <>
                      Mostrando{' '}
                      <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span>
                      {' '}–{' '}
                      <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
                      {' '}de{' '}
                      <span className="font-medium">{pagination.total}</span>
                    </>
              ) : null}
            </span>

            {/* Rows per page */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-500">Por página:</span>
              <div className="flex gap-1">
                {PAGE_SIZE_OPTIONS.map(size => (
                  <button
                    key={size}
                    onClick={() => handleLimitChange(size)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      filters.limit === size && !showingAll
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {size}
                  </button>
                ))}
                <button
                  onClick={() => handleLimitChange(ALL_LIMIT)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    showingAll
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                  }`}
                >
                  Todos
                </button>
              </div>
            </div>
          </div>

          {/* Right: page navigation (hidden when showing all) */}
          {pagination && pagination.pages > 1 && !showingAll && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Primera página"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Página anterior"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {getPageNumbers(pagination.page, pagination.pages).map((p, i) =>
                p === '...' ? (
                  <span key={`ellipsis-${i}`} className="px-1.5 py-1 text-xs text-gray-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageChange(p as number)}
                    className={`min-w-[28px] px-1.5 py-1 text-xs rounded border transition-colors ${
                      pagination.page === p
                        ? 'bg-blue-600 text-white border-blue-600 font-semibold'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:text-blue-600'
                    }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Página siguiente"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.pages)}
                disabled={pagination.page === pagination.pages}
                className="p-1.5 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                title="Última página"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterListView;
